import { ListItem, Stack, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Box, TextField, Autocomplete } from "@mui/material";
import { FC, ReactElement, useEffect, useMemo } from "react";
import { ExclusionRule } from "@/gen/api/securefile";
import StringArrayTextField from "../input/StringArrayTextField";
import { usePlainStateFormUtils } from "../input/usePlainStateFormUtils";
import { useFileInfoTypeDataContext } from "./FileInfoTypeDataProvider";
import { FormToken, useZSliceUnderContext } from "@/utils/zustand-form";
import { erSchema } from "./InfoTypeEditor.types";
import { CollectEditsMetadata, CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";


const ExclusionRuleLine : FC<{
  parentToken?: FormToken,
  className?: string,
  r_key: string,
  prevData: ExclusionRule,
  actionButton: ReactElement,
}> = ({ className, r_key, prevData, actionButton, parentToken }) => {
  const utils = useZSliceUnderContext({
    parentToken: parentToken ?? null,
    pathFragment: r_key,
    prevData,
    stageInit: () => ({ ...prevData }),
  });
  const { states, setStates, installProtocolMetadata } = utils;

  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {...prevData}, errors:{}, touched:{} }))
  }, [prevData, setStates]);
  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, erSchema);

  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => {
        const v1 = await trySubmitValidate();
        const rv = v1.stage.words ? 'words' : v1.stage.regex != null ? 'regex' : 'infoTypes';
        if (!v1.errors['regex'] && !v1.errors['words'] && !v1.errors['excludeInfoTypes']) {
          if (rv === 'words') {
            if (!v1.stage.words || v1.stage.words.length === 0) {
              v1.errors['words'] = 'Exclusion Keywords are required'
              v1.touched['words'] = true
            }
          } else if (rv === 'regex') {
            if (!v1.stage.regex) {
              v1.errors['regex'] = 'Exclusion Pattern is required'
              v1.touched['regex'] = true
            }
            v1.stage.words = null
          } else {
            if (!v1.stage.excludeInfoTypes || v1.stage.excludeInfoTypes.length === 0) {
              v1.errors['excludeInfoTypes'] = 'Exclusion Info Types are required'
              v1.touched['excludeInfoTypes'] = true
            }
          }
        }
        const ks = Object.keys(v1.errors)
        setStates({...v1})
        if (ks.length > 0) {
          throw new Error('Validation error', { cause: v1.errors })
        }
      },
    });
  }, [installProtocolMetadata, setStates, trySubmitValidate]);

  // Compute radioValue from stage data (derived state, not stored)
  const radioValue = (x: any) => (
      x.words ? 'words'
    : x.regex != null ? 'regex'
    : 'infoTypes');

  const { infoTypes } = useFileInfoTypeDataContext()
  const infoTypeOptions = useMemo(() => infoTypes.map((o) => o.name ?? ''), [infoTypes])
  const radioHandleChange = (e:any, value:string) => {
    if (value === radioValue(states.stage)) { return }
    if (value === 'words') {
      setStates(v => {
        v.stage.regex = null
        v.stage.excludeInfoTypes = null
        v.stage.words = [...(v.prevData.words ??[])]
        v.errors = { words: v.errors.words! }
        v.touched = { words: v.touched.words! };
        (v.touched as any)['kind'] = radioValue(v.prevData) !== 'words'
        return {...v}
      })
    } else if (value === "regex") {
      setStates(v => {
        v.stage.words = null
        v.stage.excludeInfoTypes = null
        v.stage.regex = v.prevData.regex ?? ''
        v.errors = { regex: v.errors.regex! }
        v.touched = { regex: v.touched.regex! };
        (v.touched as any)['kind'] = radioValue(v.prevData) !== 'regex'
        return {...v}
      })
    } else {
      setStates(v => {
        v.stage.words = null
        v.stage.regex = null
        v.stage.excludeInfoTypes = [...(v.prevData.excludeInfoTypes ??[])]
        v.errors = { excludeInfoTypes: v.errors.excludeInfoTypes! }
        v.touched = { excludeInfoTypes: v.touched.excludeInfoTypes! };
        (v.touched as any)['kind'] = radioValue(v.prevData) !== 'infoTypes'
        return {...v}
      })
    }
  }
  return (
    <ListItem className={className ?? (Object.values(states.touched).some(x => x) ? 'dirty' : undefined)}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" width="100%" marginBottom={2}>
        <FormControl sx={{ mr: 'auto'}} size="small">
          <FormLabel>Type</FormLabel>
          <RadioGroup row value={radioValue(states.stage)} onChange={radioHandleChange}>
            <FormControlLabel value="words" label="Exclusion Keywords" control={<Radio />} />
            <FormControlLabel value="regex" label="Exclusion Pattern" control={<Radio />} />
            <FormControlLabel value="infoTypes" label="Exclusion Info Types" control={<Radio />} />
          </RadioGroup>
        </FormControl>
        {actionButton}
        <Box width="100%" marginBottom={2}>
          {radioValue(states.stage) === 'words'
            ? <StringArrayTextField list={states.stage.words ?? []} stackChips="row"
                setList={(ls) => setStates(v => {
                  const words = [...new Set(ls)]
                  return ({...v,
                    stage: {...v.stage, words},
                    touched: {...v.touched, 
                      words: !!v.prevData.words !== !!v.stage.words || !!v.prevData.words && !(
                        v.prevData.words.length === words!.length &&
                        v.prevData.words.every(w => words!.indexOf(w) > -1))
                    },
                    ...(words.length === 0 ? {errors: {...v.errors, words: 'Exclusion Keywords are required'}} : (delete v.errors['words'], {errors: {...v.errors}}))
                  })
                })}
                placeholder="Add a new value (case insensitive) and press the Enter key to confirm..."
                error={Boolean(states.touched['words'] && states.errors['words'])}
                helperText={states.touched['words'] && states.errors['words']}
              />
            : radioValue(states.stage) === 'regex' ?
               <TextField fullWidth multiline {...fieldProps('regex')}
               error={Boolean(states.touched['regex'] && states.errors['regex'])}
               helperText={states.touched['regex'] && states.errors['regex']}
                placeholder="Type Regular Expression in here..." />
            : <Autocomplete multiple options={infoTypeOptions}
                getOptionLabel={(option) => option ?? ''}
                value={(states.stage.excludeInfoTypes ?? []) as string[]}
                renderInput={((params) =>
                  <TextField
                    label="Exclusion Info Types"
                    {...params}
                    fullWidth
                    error={Boolean(states.touched['excludeInfoTypes'] && states.errors['excludeInfoTypes'])}
                    helperText={states.touched['excludeInfoTypes'] && states.errors['excludeInfoTypes']}
                    {...{
                      inputProps: {
                        ...params.inputProps,
                        'aria-label': 'Exclusion Info Type',
                      }
                    }}
                  />
                )}
                onChange={(e, value, reason) =>
                  setStates(v => {
                    const excludeInfoTypes = [...new Set(value)]
                    return ({...v,
                      stage: {...v.stage, excludeInfoTypes},
                      touched: {...v.touched, 
                        excludeInfoTypes: !!v.prevData.excludeInfoTypes !== !!v.stage.excludeInfoTypes ||
                          (!!v.prevData.excludeInfoTypes && !(
                            v.prevData.excludeInfoTypes.length === excludeInfoTypes!.length &&
                            v.prevData.excludeInfoTypes.every(w => excludeInfoTypes!.indexOf(w) > -1)))
                      },
                      ...(excludeInfoTypes.length === 0 ? {errors: {...v.errors, excludeInfoTypes: 'Exclusion Info Types are required'}} : (delete v.errors['excludeInfoTypes'], {errors: {...v.errors}}))
                    })
                  })
                }
              />
        }
        </Box>
      </Stack>
    </ListItem>
  )
}

export default ExclusionRuleLine
